'use client'

import { Download, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '../ui/button'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // 1. Verificar se já está rodando em modo standalone (PWA instalado e aberto)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator &&
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true)

    if (isStandalone) return

    // 2. Verificar se o usuário dispensou o banner recentemente (últimos 7 dias)
    const pwaDismissedAt = localStorage.getItem('pwa-install-dismissed-at')
    if (pwaDismissedAt) {
      const dismissedDate = new Date(Number.parseInt(pwaDismissedAt, 10))
      const diffTime = Math.abs(new Date().getTime() - dismissedDate.getTime())
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      if (diffDays <= 7) return
    }

    // 3. Ouvir o evento do prompt de instalação disparado pelo PwaManager
    const handleInstallPrompt = (e: Event) => {
      const customEvent = e as CustomEvent<BeforeInstallPromptEvent>
      setDeferredPrompt(customEvent.detail)
      setIsVisible(true)
    }

    window.addEventListener('pwa-beforeinstallprompt', handleInstallPrompt)

    return () => {
      window.removeEventListener('pwa-beforeinstallprompt', handleInstallPrompt)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) return

    // Mostra o prompt nativo do navegador
    await deferredPrompt.prompt()

    // Aguarda a resposta de escolha do usuário
    const { outcome } = await deferredPrompt.userChoice
    console.log(`PWA escolha do usuário: ${outcome}`)

    // Limpa o prompt acumulado
    setDeferredPrompt(null)
    setIsVisible(false)
  }

  const handleDismiss = () => {
    setIsVisible(false)
    localStorage.setItem('pwa-install-dismissed-at', new Date().getTime().toString())
  }

  if (!isVisible) return null

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 z-50 max-w-md animate-in slide-in-from-bottom duration-300">
      <div className="relative overflow-hidden rounded-2xl border border-blue-500/20 dark:border-white/10 bg-white/80 dark:bg-slate-950/80 p-5 shadow-2xl backdrop-blur-xl flex flex-col gap-4">
        {/* Glow de fundo decorativo */}
        <div className="absolute -right-12 -top-12 -z-10 h-28 w-28 rounded-full bg-blue-500/10 blur-2xl" />

        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-md shadow-blue-500/10">
              <Download className="h-5 w-5 animate-pulse" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Instalar AcheiVagas
              </h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
                Adicione à sua tela inicial para buscar vagas mais rápido, com acesso offline e
                notificações.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-slate-900 dark:hover:text-white transition duration-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex justify-end gap-2.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="text-xs font-semibold text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white"
          >
            Agora não
          </Button>
          <Button
            size="sm"
            onClick={handleInstallClick}
            className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white text-xs font-semibold px-4 py-2 rounded-xl shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-all duration-200"
          >
            Instalar App
          </Button>
        </div>
      </div>
    </div>
  )
}
export default PwaInstallBanner
